// A table on a slide (S7): one box, moved, resized and layered as any box (shared/slideDesign.ts), holding a grid of
// cells a person types in. Its columns and rows are shares of the box's width and height, so a box resized keeps its
// proportions; a row grows when its words need more room. Its look comes from the deck's theme colours: the header
// row stands on the box's fill (the accent unless set), banded rows are a quiet mix of the fill and the slide, and
// borders are the box's line colour. A cell can have its own fill and ink. Merged cells (from a PowerPoint file) span
// columns and rows; the cells they cover stay in the grid, marked, so every row has every column.
//
// Plain data only, as every slide's: no CSS, addresses or HTML.

import { cleanBoxText, cleanColor, boxWords, type Align, type SlideColor, type VAlign } from "./slideDesign";
import type { DocBody, DocNode } from "./docblocks";
import { inlineMarkdown } from "./docmd";
import { cleanCellFormat, cleanTableFormat, type CellFormat, type TableFormat } from "./slideTableFormat";

export interface TableCell extends CellFormat {
  /** its words: paragraphs and lists, with the marks a box keeps */
  text?: DocBody | null;
  /** its own fill (null: none), over the table's */
  fill?: SlideColor | null;
  /** its own ink */
  color?: SlideColor;
  align?: Align;
  valign?: VAlign;
  /** the columns and rows it spans, from its top left (merged cells) */
  cs?: number;
  rs?: number;
  /** a cell covered by a merged one */
  m?: true;
}

export interface TableRow {
  /** its share of the table's height, the rows' summing to 1 (a row grows with its words) */
  h: number;
  cells: TableCell[];
}

export interface SlideTable extends TableFormat {
  /** each column's share of the table's width, summing to 1 */
  cols: number[];
  rows: TableRow[];
  /** the first row is a header row: on the box's fill, its words bold */
  head?: boolean;
  /** every other row is shaded */
  band?: boolean;
}

/** The most columns and rows a table holds, and the words a cell holds. */
export const MAX_TABLE_COLS = 12;
export const MAX_TABLE_ROWS = 40;
export const MAX_CELL_TEXT = 1_000;
/** The words a whole table holds. */
export const MAX_TABLE_TEXT = 8_000;
/** The narrowest a column is, as a share of the table. */
export const MIN_COL = 0.03;

const share = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null);
const ALIGNS = ["left", "center", "right", "justify"] as const;
const VALIGNS = ["top", "middle", "bottom"] as const;

/** Shares as kept: each at least a little, summing to 1, rounded to four places. */
export function normShares(raw: (number | null)[], n: number): number[] {
  const vals = Array.from({ length: n }, (_, i) => share(raw[i]) ?? 0);
  const known = vals.filter((v) => v > 0);
  const fill = known.length ? known.reduce((a, b) => a + b, 0) / known.length : 1;
  const filled = vals.map((v) => (v > 0 ? v : fill));
  const sum = filled.reduce((a, b) => a + b, 0) || 1;
  const min = Math.min(MIN_COL, 1 / n);
  const out = filled.map((v) => Math.max(min, v / sum));
  const again = out.reduce((a, b) => a + b, 0);
  return out.map((v) => Math.round((v / again) * 10_000) / 10_000);
}

function cleanCell(raw: unknown): TableCell {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const r = raw as Record<string, unknown>;
  const out: TableCell = {};
  if (r.m === true) return { m: true };
  const text = cleanBoxText(r.text);
  if (text) out.text = text;
  if (Object.hasOwn(r, "fill")) {
    if (r.fill === null || r.fill === "none") out.fill = null;
    else {
      const c = cleanColor(r.fill);
      if (c) out.fill = c;
    }
  }
  const color = cleanColor(r.color);
  if (color) out.color = color;
  if ((ALIGNS as readonly unknown[]).includes(r.align)) out.align = r.align as Align;
  if ((VALIGNS as readonly unknown[]).includes(r.valign)) out.valign = r.valign as VAlign;
  const span = (v: unknown, max: number) => (typeof v === "number" && Number.isInteger(v) && v > 1 ? Math.min(v, max) : undefined);
  const cs = span(r.cs, MAX_TABLE_COLS);
  const rs = span(r.rs, MAX_TABLE_ROWS);
  if (cs) out.cs = cs;
  if (rs) out.rs = rs;
  // SB: the cell's own borders (shared/slideTableFormat.ts)
  Object.assign(out, cleanCellFormat(r));
  return out;
}

/**
 * A table as kept, or null when it has no rows or columns. Rows are made as wide as the table (cells added or cut),
 * spans kept inside the grid, and the cells a span covers marked; shares summing to 1.
 */
export function cleanTable(raw: unknown): SlideTable | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const rowsRaw = Array.isArray(r.rows) ? r.rows.slice(0, MAX_TABLE_ROWS) : [];
  const colsRaw = Array.isArray(r.cols) ? r.cols.slice(0, MAX_TABLE_COLS) : [];
  const widest = Math.max(colsRaw.length, ...rowsRaw.map((row) => (row && typeof row === "object" && Array.isArray((row as TableRow).cells) ? (row as TableRow).cells.length : 0)));
  const n = Math.min(MAX_TABLE_COLS, widest);
  if (!n || !rowsRaw.length) return null;
  const cols = normShares(colsRaw.map((c) => share(c)), n);
  const hs = normShares(rowsRaw.map((row) => share((row as TableRow | null)?.h)), rowsRaw.length);
  const rows: TableRow[] = rowsRaw.map((row, i) => {
    const cellsRaw = row && typeof row === "object" && Array.isArray((row as TableRow).cells) ? (row as TableRow).cells : [];
    const cells = Array.from({ length: n }, (_, j) => cleanCell(cellsRaw[j]));
    return { h: hs[i], cells };
  });
  // spans stay in the grid, and what they cover is marked (and only that)
  const covered = rows.map(() => new Array<boolean>(n).fill(false));
  rows.forEach((row, i) =>
    row.cells.forEach((c, j) => {
      if (c.m || covered[i][j]) return;
      const cs = Math.min(c.cs ?? 1, n - j);
      const rs = Math.min(c.rs ?? 1, rows.length - i);
      if (cs > 1) c.cs = cs;
      else delete c.cs;
      if (rs > 1) c.rs = rs;
      else delete c.rs;
      for (let a = 0; a < rs; a++) for (let b = 0; b < cs; b++) if (a || b) covered[i + a][j + b] = true;
    }),
  );
  rows.forEach((row, i) =>
    row.cells.forEach((c, j) => {
      if (covered[i][j]) row.cells[j] = { m: true };
      else if (c.m) row.cells[j] = {};
    }),
  );
  const out: SlideTable = { cols, rows };
  if (r.head === true) out.head = true;
  if (r.band === true) out.band = true;
  // SB: a table style and its first column
  Object.assign(out, cleanTableFormat(r));
  return out;
}

/** A cell's words on one line. */
export const cellWords = (c: TableCell) => boxWords(c.text);

/** A table's words, row by row, for limits, search and replies. */
export function tableWords(t: SlideTable | null | undefined): string {
  return (t?.rows ?? [])
    .map((r) => r.cells.map(cellWords).filter(Boolean).join(" "))
    .filter(Boolean)
    .join(" ");
}

/** Why a table cannot be kept, in words; null when it can. */
export function tableLimits(t: SlideTable, id: string): string | null {
  for (const r of t.rows)
    for (const c of r.cells) {
      const n = cellWords(c).length;
      if (n > MAX_CELL_TEXT) return `A table's cell holds up to ${MAX_CELL_TEXT.toLocaleString("en-US")} characters; one in table ${id} has ${n.toLocaleString("en-US")}.`;
    }
  const all = tableWords(t).length;
  if (all > MAX_TABLE_TEXT) return `A table holds up to ${MAX_TABLE_TEXT.toLocaleString("en-US")} characters; table ${id} has ${all.toLocaleString("en-US")}. Split it across slides.`;
  return null;
}

/** A cell's words from plain text: one paragraph a line. */
export function cellText(words: string): DocBody | null {
  const lines = words.split(/\r?\n/);
  const content: DocNode[] = lines.map((l) => (l ? { type: "paragraph", content: [{ type: "text", text: l }] } : { type: "paragraph" }));
  return content.some((p) => p.content?.length) ? { type: "doc", content } : null;
}

/** A new table of rows by columns, empty, the first row a header. */
export function newTable(rows: number, cols: number): SlideTable {
  const r = Math.max(1, Math.min(MAX_TABLE_ROWS, Math.round(rows)));
  const c = Math.max(1, Math.min(MAX_TABLE_COLS, Math.round(cols)));
  return cleanTable({ cols: new Array(c).fill(1), rows: Array.from({ length: r }, () => ({ h: 1, cells: Array.from({ length: c }, () => ({})) })), head: true })!;
}

// ---------------------------------------------------------------------------
// changing a table: each returns a new table, cleaned (rows and columns kept within the limits)

/** A table with its merged cells taken apart (the words stay in the top left one). */
export function unmerged(t: SlideTable): SlideTable {
  if (!t.rows.some((r) => r.cells.some((c) => c.m || c.cs || c.rs))) return t;
  return {
    ...t,
    rows: t.rows.map((r) => ({ ...r, cells: r.cells.map((c) => (c.m ? {} : (({ cs: _cs, rs: _rs, ...rest }) => rest)(c))) })),
  };
}

// SB: rows and columns added and taken out keep the merged cells, as PowerPoint does: a merge a new row or column goes
// through grows over it, one that loses a row or column shrinks, and a merge whose first row or column goes keeps its
// words in the cell that takes its place. A table is read as its cells that are not covered (anchors), changed, and
// laid out again.

interface Anchor {
  r: number;
  c: number;
  cell: TableCell;
}

const anchorsOf = (t: SlideTable): Anchor[] => t.rows.flatMap((row, r) => row.cells.flatMap((cell, c) => (cell.m ? [] : [{ r, c, cell }])));

function layOut(t: SlideTable, cols: number[], heights: number[], anchors: Anchor[]): SlideTable {
  const rows: TableRow[] = heights.map((h) => ({ h, cells: cols.map(() => ({}) as TableCell) }));
  for (const a of anchors) if (rows[a.r] && a.c < cols.length) rows[a.r].cells[a.c] = a.cell;
  return cleanTable({ ...t, cols, rows })!;
}

/** A cell's look without its words or span: what a new row or column copies from its neighbour. */
const lookOnly = (c: TableCell | undefined): TableCell => {
  if (!c || c.m) return {};
  const { text: _t, cs: _cs, rs: _rs, m: _m, ...rest } = c;
  return rest;
};

/** A row added before row `at` (at the end when `at` is the count), as tall as its neighbour and in its look. */
export function addRow(t: SlideTable, at: number): SlideTable {
  if (t.rows.length >= MAX_TABLE_ROWS) return t;
  const i = Math.max(0, Math.min(t.rows.length, at));
  const like = t.rows[Math.min(i, t.rows.length - 1)] ?? t.rows[i - 1];
  const anchors = anchorsOf(t).map((a) => {
    const rs = a.cell.rs ?? 1;
    if (a.r >= i) return { ...a, r: a.r + 1 };
    if (a.r < i && a.r + rs > i) return { ...a, cell: { ...a.cell, rs: rs + 1 } };
    return a;
  });
  const covered = new Set<number>();
  for (const a of anchors) if (a.r < i && a.r + (a.cell.rs ?? 1) > i) for (let k = 0; k < (a.cell.cs ?? 1); k++) covered.add(a.c + k);
  t.cols.forEach((_, c) => {
    if (!covered.has(c)) anchors.push({ r: i, c, cell: lookOnly(like?.cells[c]) });
  });
  const hs = t.rows.map((r) => r.h);
  hs.splice(i, 0, like?.h ?? 1 / (t.rows.length + 1));
  return layOut(t, t.cols, hs, anchors);
}

/** Row `at` taken out (a table keeps one row). */
export function removeRow(t: SlideTable, at: number): SlideTable {
  if (t.rows.length <= 1 || at < 0 || at >= t.rows.length) return t;
  const anchors = anchorsOf(t).flatMap((a): Anchor[] => {
    const rs = a.cell.rs ?? 1;
    if (a.r === at) return rs > 1 ? [{ ...a, cell: { ...a.cell, rs: rs - 1 } }] : [];
    if (a.r < at && a.r + rs > at) return [{ ...a, cell: { ...a.cell, rs: rs - 1 } }];
    return [a.r > at ? { ...a, r: a.r - 1 } : a];
  });
  // the header row taken out, the next row is the header (as PowerPoint's Header Row stays on)
  return layOut(t, t.cols, t.rows.filter((_, i) => i !== at).map((r) => r.h), anchors);
}

/** A column added before column `at`, as wide as its neighbour (the others give it room) and in its look. */
export function addCol(t: SlideTable, at: number): SlideTable {
  if (t.cols.length >= MAX_TABLE_COLS) return t;
  const i = Math.max(0, Math.min(t.cols.length, at));
  const likeAt = Math.min(i, t.cols.length - 1);
  const anchors = anchorsOf(t).map((a) => {
    const cs = a.cell.cs ?? 1;
    if (a.c >= i) return { ...a, c: a.c + 1 };
    if (a.c < i && a.c + cs > i) return { ...a, cell: { ...a.cell, cs: cs + 1 } };
    return a;
  });
  const covered = new Set<number>();
  for (const a of anchors) if (a.c < i && a.c + (a.cell.cs ?? 1) > i) for (let k = 0; k < (a.cell.rs ?? 1); k++) covered.add(a.r + k);
  t.rows.forEach((row, r) => {
    if (!covered.has(r)) anchors.push({ r, c: i, cell: lookOnly(row.cells[likeAt]?.m ? undefined : row.cells[likeAt]) });
  });
  const cols = [...t.cols];
  cols.splice(i, 0, t.cols[likeAt] ?? 1 / (t.cols.length + 1));
  return layOut(t, cols, t.rows.map((r) => r.h), anchors);
}

/** Column `at` taken out (a table keeps one column). */
export function removeCol(t: SlideTable, at: number): SlideTable {
  if (t.cols.length <= 1 || at < 0 || at >= t.cols.length) return t;
  const anchors = anchorsOf(t).flatMap((a): Anchor[] => {
    const cs = a.cell.cs ?? 1;
    if (a.c === at) return cs > 1 ? [{ ...a, cell: { ...a.cell, cs: cs - 1 } }] : [];
    if (a.c < at && a.c + cs > at) return [{ ...a, cell: { ...a.cell, cs: cs - 1 } }];
    return [a.c > at ? { ...a, c: a.c - 1 } : a];
  });
  return layOut(t, t.cols.filter((_, i) => i !== at), t.rows.map((r) => r.h), anchors);
}

/** Cells in a rectangle changed by `fn`. */
export function setCells(t: SlideTable, r0: number, c0: number, r1: number, c1: number, fn: (c: TableCell) => TableCell): SlideTable {
  const [ra, rb] = [Math.min(r0, r1), Math.max(r0, r1)];
  const [ca, cb] = [Math.min(c0, c1), Math.max(c0, c1)];
  return { ...t, rows: t.rows.map((r, i) => (i < ra || i > rb ? r : { ...r, cells: r.cells.map((c, j) => (j < ca || j > cb || c.m ? c : fn(c))) })) };
}

/** The border between column `at` and the next moved by a share of the table's width; both keep at least MIN_COL. */
export function moveColEdge(t: SlideTable, at: number, by: number): SlideTable {
  if (at < 0 || at >= t.cols.length - 1) return t;
  const a = t.cols[at];
  const b = t.cols[at + 1];
  const d = Math.max(MIN_COL - a, Math.min(b - MIN_COL, by));
  const cols = t.cols.map((w, i) => (i === at ? a + d : i === at + 1 ? b - d : w));
  return { ...t, cols: cols.map((w) => Math.round(w * 10_000) / 10_000) };
}

/** Every column as wide as the others. */
export const evenCols = (t: SlideTable): SlideTable => ({ ...t, cols: normShares(t.cols.map(() => 1), t.cols.length) });

/** The cell after (or before) one, row by row, past the cells a merge covers; null at the table's end. */
export function nextCell(t: SlideTable, r: number, c: number, back = false): { r: number; c: number } | null {
  const n = t.cols.length;
  let i = r * n + c;
  for (;;) {
    i += back ? -1 : 1;
    if (i < 0 || i >= t.rows.length * n) return null;
    const rr = Math.floor(i / n);
    const cc = i % n;
    if (!t.rows[rr].cells[cc]?.m) return { r: rr, c: cc };
  }
}

// ---------------------------------------------------------------------------
// Markdown: a table on a slide is a Markdown table (GitHub's), a row a line, cells between pipes

/** A cell's words as Markdown on one line: its marks kept, its paragraphs and points side by side. */
const cellMd = (c: TableCell) => {
  const inline: DocNode[] = [];
  const walk = (n: DocNode) => {
    if (n.type === "paragraph") {
      if (inline.length) inline.push({ type: "text", text: " " });
      inline.push(...(n.content ?? []).map((x) => (x.type === "hardBreak" ? { type: "text", text: " " } : x)));
    } else for (const k of n.content ?? []) walk(k);
  };
  for (const b of c.text?.content ?? []) walk(b);
  return inlineMarkdown(inline, {}, true).replace(/\s+/g, " ").trim();
};

/** A table as a Markdown table: the first row as its header (a table with no header row gets an empty one). */
export function tableToMarkdown(t: SlideTable): string {
  const n = t.cols.length;
  const line = (cells: string[]) => `| ${cells.join(" | ")} |`;
  const rows = t.rows.map((r) => r.cells.slice(0, n).map((c) => (c.m ? "" : cellMd(c))));
  const align = (j: number) => {
    const a = t.rows.map((r) => r.cells[j]?.align).find(Boolean);
    return a === "center" ? ":---:" : a === "right" ? "---:" : "---";
  };
  const head = t.head ? rows[0] : new Array(n).fill("");
  const body = t.head ? rows.slice(1) : rows;
  return [line(head), line(Array.from({ length: n }, (_, j) => align(j))), ...body.map(line)].join("\n");
}

const SEP = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;

/** A line's cells between its pipes (an escaped pipe kept as one). */
export function mdCells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const out: string[] = [];
  let cur = "";
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\" && s[i + 1] === "|") {
      cur += "|";
      i++;
    } else if (s[i] === "|") {
      out.push(cur.trim());
      cur = "";
    } else cur += s[i];
  }
  out.push(cur.trim());
  return out;
}

/** Whether some lines start a Markdown table: a row of cells, then a line of dashes under them. */
export const isMdTable = (lines: string[], i = 0) => /\|/.test(lines[i] ?? "") && SEP.test(lines[i + 1] ?? "") && mdCells(lines[i]).length === mdCells(lines[i + 1]).length;

/** A Markdown table as a table (its header row a header row, unless it is empty), or null when the lines are not one. */
export function tableFromMarkdown(lines: string[], inline: (s: string) => DocNode[] = (s) => (s ? [{ type: "text", text: s }] : [])): SlideTable | null {
  if (!isMdTable(lines)) return null;
  const head = mdCells(lines[0]);
  const aligns = mdCells(lines[1]).map((a) => (/^:-+:$/.test(a) ? "center" : /^-+:$/.test(a) ? "right" : undefined));
  const n = Math.min(MAX_TABLE_COLS, head.length);
  const body = lines.slice(2).filter((l) => /\|/.test(l)).map(mdCells);
  const blankHead = head.every((h) => !h);
  const raw = [...(blankHead ? [] : [head]), ...body].slice(0, MAX_TABLE_ROWS);
  if (!raw.length) return null;
  const cell = (s: string, j: number): TableCell => {
    const content = inline(s.slice(0, MAX_CELL_TEXT));
    return { ...(content.length ? { text: { type: "doc", content: [{ type: "paragraph", content }] } } : {}), ...(aligns[j] ? { align: aligns[j] } : {}) };
  };
  return cleanTable({ cols: new Array(n).fill(1), rows: raw.map((r) => ({ h: 1, cells: Array.from({ length: n }, (_, j) => cell(r[j] ?? "", j)) })), head: !blankHead });
}

/** Every cell's words changed by `fn` (undefined leaves a cell as it is, null clears its words): the bar's marks on a whole table. */
export function mapCellText(t: SlideTable, fn: (text: DocBody | null) => DocBody | null | undefined): SlideTable {
  return {
    ...t,
    rows: t.rows.map((r) => ({
      ...r,
      cells: r.cells.map((c) => {
        if (c.m) return c;
        const nt = fn(c.text ?? null);
        if (nt === undefined) return c;
        const out = { ...c };
        if (nt) out.text = nt;
        else delete out.text;
        return out;
      }),
    })),
  };
}

/** A table's words as one body, for reading whether all of them carry a mark. */
export const tableBody = (t: SlideTable): DocBody => ({ type: "doc", content: t.rows.flatMap((r) => r.cells.flatMap((c) => c.text?.content ?? [])) });
